import React, { useCallback, useMemo } from "react";
import cx from "classnames";
import { useIndentComponent } from "components/ProjectTree/components/useIndentComponent";
import { useSubType } from "objs/_ConfiguratorObjs/Wizard/stores/wizardStore";
import { WizardTopologyMenuNodeLayout } from "./WizardTopologyMenuNodeLayOut";

export const WizardTopologyMenuNode = React.memo(
  ({ translations, node, style, options }) => {
    const { data } = node;
    const subType = useSubType();

    const handleToggleDetails = useCallback(
      (e) => {
        e.stopPropagation();
        node.toggle();

        node.children?.forEach((child) => {
          // Added to catch if any of the children has a product name that contains "(2/N)" to also uncollapse them
          // No way to change the structure on EPF side to avoid this
          // https://dev.azure.com/DEVLY-IT-Software-Development/TROX-EB-Website_v2/_workitems/edit/50145
          if (
            child.children[0] &&
            /\(2\/\d\)/.test(child.children[0]?.data.productName)
          ) {
            child.toggle();
          }
          if (
            child.children[0] &&
            child.children[0]?.data.productName === "" &&
            child.data.kind === "component"
          ) {
            child.toggle();
          }
        });
      },
      [node],
    );

    const hasChildren = useMemo(() => {
      const mainChildren = [
        ...(data.sameLayer || []),
        ...(data.connectedByTopBracketChildren || []),
        ...(data.bottomPlacedChildren || []),
        ...(data.rightPlacedChildren || []),
      ];

      const extraChildren = [
        ...(data.directlyConnected || []),
        ...(data.chainedChildren || []),
      ];

      const levelThreshold = subType === "roomcontrolwireless" ? 1 : 2;

      // Only include extra children at top level and first level
      if (node.level <= levelThreshold) {
        return [...mainChildren, ...extraChildren].length > 0;
      }

      return mainChildren.length > 0;
    }, [data, node, subType]);

    const isSiblingChild = useMemo(() => {
      const parentLevelThreshold = subType === "roomcontrolwireless" ? 1 : 2;

      return (
        node.parent.level > parentLevelThreshold &&
        (node.parent?.data?.directlyConnected?.includes(node.data) ||
          node.parent?.data?.chainedChildren?.includes(node.data))
      );
    }, [node, subType]);

    const { level, isOpen } = node;

    const leftPad =
      (style.paddingLeft ?? 0) - (isSiblingChild ? options.indentSize : 0);

    const indent = useIndentComponent(node, options, isSiblingChild, subType);

    return (
      <div
        style={{
          ...style,
          paddingLeft: leftPad,
        }}
        className={cx("w-100 h-100", {
          "pe-2": !options.isFullpage,
        })}
      >
        <div>
          {indent}
          <WizardTopologyMenuNodeLayout
            presentation={data?.presentation}
            handleToggle={handleToggleDetails}
            hasChildren={hasChildren}
            data={data}
            isIconsShown={false}
            isOpen={isOpen}
            translations={translations}
            className="position-relative"
            level={level}
            options={options}
          />
        </div>
      </div>
    );
  },
);
